import React, { useState } from "react";
import { toast } from "sonner";
import useFetch from "@/lib/useFetch";
import api from "@/lib/api";
import { useAdmin } from "@/context/AdminContext";
import { PageHeader, Footer, SectionCard } from "@/components/common/Page";
import Loader from "@/components/common/Loader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { timeAgo } from "@/lib/communities";
import { Coins, Plus, Minus } from "lucide-react";

const CUR = { scrap: "Scrap", hc: "HC", credits: "Credits" };

export default function Economy() {
    const { community } = useAdmin();
    const { data: communities } = useFetch("/communities");
    const { data: players } = useFetch("/players");
    const { data: wallets, reload: reloadWallets } = useFetch("/admin/wallets");
    const { data: txns, reload: reloadTxns } = useFetch("/admin/transactions");
    const { data: discord, reload: reloadDiscord } = useFetch("/discord/status");
    const [form, setForm] = useState({ user_email: "", community_key: "CODERS_PLAYGROUND", currency: "scrap", amount: 1000, reason: "Admin adjustment" });
    if (!communities || !players || !wallets || !txns || !discord) return <Loader />;

    const upd = (k, v) => setForm({ ...form, [k]: v });
    const nameFor = (k) => communities.find((c) => c.key === k)?.name || k;
    const filteredW = community === "ALL" ? wallets : wallets.filter((w) => w.community_key === community);
    const filteredT = community === "ALL" ? txns : txns.filter((t) => t.community_key === community);

    const adjust = async (sign) => {
        if (!form.user_email) { toast.error("Select a player"); return; }
        try {
            const { data } = await api.post("/admin/economy/adjust", { ...form, amount: sign * Math.abs(+form.amount) });
            toast.success("Balance adjusted", { description: `New balance: ${data.balance.toLocaleString()} ${CUR[form.currency]}` });
            reloadWallets(); reloadTxns();
        } catch (e) { toast.error("Failed", { description: e.response?.data?.detail || "" }); }
    };

    const toggleLog = async (v) => {
        await api.post("/admin/economy/discord-log", { economy_discord_log: v });
        reloadDiscord();
        toast.success(`Discord economy logging ${v ? "enabled" : "disabled"}`);
    };

    return (
        <div data-testid="admin-economy">
            <PageHeader title="Economy" subtitle="Per-community wallets, adjustments and transactions." />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
                <SectionCard title="Admin Adjustment" testId="economy-adjust">
                    <div className="space-y-3">
                        <div>
                            <label className="label-mono text-muted-foreground">Player</label>
                            <Select value={form.user_email} onValueChange={(v) => upd("user_email", v)}>
                                <SelectTrigger data-testid="adjust-player" className="bg-background border-border mt-1"><SelectValue placeholder="Select player" /></SelectTrigger>
                                <SelectContent>{players.map((p) => <SelectItem key={p.id} value={p.email}>{p.username} ({p.email})</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="label-mono text-muted-foreground">Community</label>
                                <Select value={form.community_key} onValueChange={(v) => upd("community_key", v)}>
                                    <SelectTrigger data-testid="adjust-community" className="bg-background border-border mt-1"><SelectValue /></SelectTrigger>
                                    <SelectContent>{communities.map((c) => <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="label-mono text-muted-foreground">Currency</label>
                                <Select value={form.currency} onValueChange={(v) => upd("currency", v)}>
                                    <SelectTrigger data-testid="adjust-currency" className="bg-background border-border mt-1"><SelectValue /></SelectTrigger>
                                    <SelectContent>{Object.entries(CUR).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <label className="label-mono text-muted-foreground">Amount</label>
                            <Input data-testid="adjust-amount" type="number" value={form.amount} onChange={(e) => upd("amount", e.target.value)} className="bg-background border-border mt-1" />
                        </div>
                        <div>
                            <label className="label-mono text-muted-foreground">Reason</label>
                            <Input value={form.reason} onChange={(e) => upd("reason", e.target.value)} className="bg-background border-border mt-1" />
                        </div>
                        <div className="flex gap-2">
                            <Button data-testid="adjust-add" onClick={() => adjust(1)} className="flex-1 font-heading uppercase bg-radioactive/90 text-black hover:bg-radioactive"><Plus size={14} className="mr-1" /> Add</Button>
                            <Button data-testid="adjust-remove" onClick={() => adjust(-1)} variant="outline" className="flex-1 font-heading uppercase border-danger/40 text-danger hover:bg-danger/10"><Minus size={14} className="mr-1" /> Remove</Button>
                        </div>
                    </div>
                </SectionCard>

                <SectionCard title="Settings" testId="economy-settings">
                    <div className="flex items-center justify-between rounded-md border border-border bg-background/40 p-4">
                        <div>
                            <div className="font-heading font-bold uppercase text-foreground">Discord Economy Logging</div>
                            <p className="text-sm text-muted-foreground">Post purchases to the mapped #economy-logs channel.</p>
                        </div>
                        <Switch data-testid="econ-log-toggle" checked={discord.economy_discord_log} onCheckedChange={toggleLog} />
                    </div>
                    <div className="mt-4">
                        <div className="label-mono text-muted-foreground mb-2">Wallets ({filteredW.length})</div>
                        <div className="max-h-56 overflow-y-auto space-y-1">
                            {filteredW.map((w) => (
                                <div key={w.id} className="flex items-center justify-between rounded-md border border-border bg-background/40 p-2 text-sm">
                                    <span className="text-foreground truncate">{w.user_email}</span>
                                    <span className="label-mono text-muted-foreground">{nameFor(w.community_key)}</span>
                                    <span className="font-heading font-bold text-radioactive">{(w.scrap || 0).toLocaleString()}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </SectionCard>
            </div>

            <SectionCard title="Recent Transactions" testId="economy-transactions">
                <div className="space-y-1.5">
                    {filteredT.length === 0 && <div className="text-sm text-muted-foreground">No transactions yet.</div>}
                    {filteredT.slice(0, 40).map((t) => (
                        <div key={t.id} className="flex items-center justify-between rounded-md border border-border bg-background/40 p-2.5 text-sm">
                            <div className="flex items-center gap-2 min-w-0">
                                <Coins size={14} className="text-hazard" />
                                <span className="text-foreground truncate">{t.user_email}</span>
                                <span className="label-mono text-muted-foreground">{t.reason}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className={`font-heading font-bold ${t.amount >= 0 ? "text-radioactive" : "text-danger"}`}>{t.amount >= 0 ? "+" : ""}{t.amount.toLocaleString()} {CUR[t.currency]}</span>
                                <span className="label-mono text-muted-foreground hidden sm:inline">{timeAgo(t.created_at)}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </SectionCard>
            <Footer />
        </div>
    );
}
